import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TeModal,
  TeText,
} from '@topcon/tuicl-react';
import { useCallback, useMemo, useState } from 'react';
import { PlaceholderContainer, useAuthentication, useNotification } from '@topcon/topcon-utils';

import { useDeactivateAccount } from './businessPartner.mutations';
import { Section } from '../../utils/styling';
import Header from '../../components/Header';
import CompanyTree from '../../components/Company/CompanyTree';
import { useCurrentIdentityBusinessPartners } from './businessPartner.queries';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';

const DeactivateAccount = () => {
  const { t } = useTranslation('settings');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  const authenticationService = useAuthentication();

  const { isPending, mutateAsync: deactivateAccount } = useDeactivateAccount();

  const {
    data: businessPartners,
    isPending: isPendingBusinessPartners,
    refetch,
  } = useCurrentIdentityBusinessPartners();

  const ownedCompanies = useMemo(() => {
    if (!businessPartners) return [];
    const companies = [...(businessPartners?.items || [])].sort((a, b) =>
      a.name.localeCompare(b.name, 'fi', { numeric: true })
    );
    return companies.filter(
      (company) => company.ownerIdentityId === authenticationService.identityId
    );
  }, [businessPartners, authenticationService.identityId]);

  const isAuthorized = useMemo(() => {
    return ownedCompanies.length === 0;
  }, [ownedCompanies]);

  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

  const openModal = useCallback(() => setOpenConfirmDialog(true), []);
  const closeModal = useCallback(() => setOpenConfirmDialog(false), []);

  const onConfirmClick = useCallback(async () => {
    try {
      await deactivateAccount();

      closeModal();

      authenticationService.signOut();
    } catch (error: unknown) {
      if (showError(error)) {
        return;
      }
      notificationService.addNotification(
        t('deactivateAccount.requestFailedTitle'),
        t('deactivateAccount.requestFailedDescription'),
        'error'
      );
    }
  }, [deactivateAccount, t, notificationService, authenticationService, closeModal, showError]);

  const renderDescription = useCallback(({ icon, text }: { icon: string; text: string }) => {
    return (
      <TeContainerSlim
        fill
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="xs"
        style={{ flexGrow: 1, display: 'flex' }}
      >
        <TeIcon size="m" name={icon} style={{ paddingTop: '1px' }} />
        <div style={{ flex: '1 !important', display: 'block !important' }}>
          <TeText size={14} style={{ lineHeight: '18px' }}>
            <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
          </TeText>
        </div>
      </TeContainerSlim>
    );
  }, []);

  const renderLoader = useCallback(() => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={8}
        column
        fill
        justifyContent="center"
        alignItems="center"
        padding="m"
        gap="s"
      >
        <PlaceholderContainer hasLoader text={t('deactivateAccount.loadingPermissions')} />
      </TeContainerSlim>
    );
  }, [t]);

  const renderNotAuthorized = useCallback(() => {
    return (
      <>
        <TeContainerSlim
          backgroundColor="background.secondary"
          borderRadius={8}
          column
          fill
          justifyContent="center"
          alignItems="flex-start"
          padding="m"
          gap="m"
        >
          <TeText weight={700} size={16} style={{ lineHeight: '20px' }}>
            {t('deactivateAccount.titleConfirmationUnauthorized')}
          </TeText>
          {renderDescription({
            icon: 'arrow_circle_right',
            text: t('deactivateAccount.descriptionUnauthorized'),
          })}
          {renderDescription({
            icon: 'arrow_circle_right',
            text: t('deactivateAccount.descriptionUnauthorized2'),
          })}
        </TeContainerSlim>
        <CompanyTree type="companies" allowSignIn companies={ownedCompanies} onUpdate={refetch} />
      </>
    );
  }, [ownedCompanies, refetch, renderDescription, t]);

  const renderAuthorized = useCallback(() => {
    return (
      <>
        <TeContainerSlim
          backgroundColor="background.secondary"
          borderRadius={8}
          column
          fill
          justifyContent="center"
          alignItems="flex-start"
          padding="m"
          gap="s"
        >
          <TeText weight={700} size={16} style={{ lineHeight: '20px' }}>
            {t('deactivateAccount.titleConfirmation')}
          </TeText>
          {renderDescription({
            icon: 'arrow_circle_right',
            text: t('deactivateAccount.description'),
          })}
        </TeContainerSlim>
      </>
    );
  }, [t, renderDescription]);

  return (
    <>
      <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
        <Header text={t('deactivateAccount.title')} />

        <Section>
          <TeContainer column fill gap="m">
            {isPendingBusinessPartners
              ? renderLoader()
              : isAuthorized
                ? renderAuthorized()
                : renderNotAuthorized()}
          </TeContainer>

          <TeDivider dividerColor="border.secondary" />

          <TeContainer fill justifyContent="flex-end">
            <TeButton
              icon="warning_amber"
              text={t('deactivateAccount.requestButton')}
              size="s"
              variant="tertiary"
              style={{
                fontWeight: '400 !important',
              }}
              disabled={!isAuthorized || isPending || isPendingBusinessPartners}
              teclick={openModal}
            />
          </TeContainer>
        </Section>
      </TeContainer>
      {openConfirmDialog && (
        <TeModal
          variant="dark"
          open={openConfirmDialog}
          closeable={!isPending}
          closeMode="button"
          teclose={() => closeModal()}
          modalTitle={t('deactivateAccount.modalTitle')}
          contentPadding={0}
        >
          <TeContainer backgroundColor="background.primary" width={480} padding="m" fill>
            {renderDescription({ icon: 'info', text: t('deactivateAccount.modalContent') })}
          </TeContainer>
          <TeButton
            slot="tertiary"
            text={t('cancel')}
            variant="neutral"
            teclick={() => closeModal()}
            size="s"
          />
          <TeButton
            slot="primary"
            text={t('deactivateAccount.modalSend')}
            variant="tertiary"
            teclick={onConfirmClick}
            loading={isPending}
            size="s"
          />
        </TeModal>
      )}
    </>
  );
};

export default DeactivateAccount;
